import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { getIsMultiHCEnabled } from '@helpCenter/util/env';
import { LoadingSkeleton } from '@helpCenter/view/common/loading';
import ErrorBoundary from '@helpCenter/view/error-boundary/error-boundary';
import { useIntl } from 'react-intl-next';
import { LazySuspense } from 'react-loosely-lazy';
import type { PreloadedQuery } from 'react-relay';
import * as grid from 'view/styles/grid';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EditorSettingsIcon from '@atlaskit/icon/glyph/editor/settings';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Button } from '@atlassian/help-center-common-component/analytics';
import {
    EngagementMessagesTarget,
    HELP_CENTERS_MANAGEMENT_BASE_PATH,
} from '@atlassian/help-center-common-component/constants';
import { ErrorMessage } from '@atlassian/help-center-common-component/error-message';
import { RenderNextAnimationFrame } from '@atlassian/help-center-common-component/render-next-animation-frame';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { MultiHcDropdownContentComponent } from '../multi-hc-dropdown-content';
import type { multiHcDropdownContentComponentQuery } from '../multi-hc-dropdown-content/__generated__/multiHcDropdownContentComponentQuery.graphql';
import { NoUnderlineDropdownItemWithAnalytics } from '../no-underline-dropdown-item-with-analytics';
import { CHARACTER_LIMIT_MULTI_HC_DROPDOWN_ITEMS } from './constants';
import { multiHcDropdownExperience } from './experiences';
import messages from './messages';

interface Props {
    currentHcName: string;
    onCreateHcClick?: () => void;
    onHelpCenterClick?: () => void;
    showMultiHCChangeBoardingSpotlight?: boolean;
    queryRef: PreloadedQuery<multiHcDropdownContentComponentQuery> | null;
}

export const MultiHcDropdown: React.FC<Props> = ({
    currentHcName,
    onCreateHcClick,
    onHelpCenterClick,
    showMultiHCChangeBoardingSpotlight,
    queryRef,
}) => {
    const { formatMessage } = useIntl();
    useUFOComponentExperience(multiHcDropdownExperience);
    const [isSeparatorVisible, setIsSeparatorVisible] = useState(true);
    const [isOpen, setIsOpen] = useState(false);

    const onClickIsOpen = () => {
        if (showMultiHCChangeBoardingSpotlight) return;
        setIsOpen(!isOpen);
    };

    const renderError = useCallback(() => {
        return (
            <Box xcss={multiHcDropdownErrorStyle}>
                <ErrorMessage title={formatMessage(messages.genericErrorHeading)} message={''} />
            </Box>
        );
    }, [formatMessage]);

    const toggleSeparatorVisibility = (isVisible: boolean) => {
        setIsSeparatorVisible(isVisible);
    };

    const helpCenterManagementScreenUrl = HELP_CENTERS_MANAGEMENT_BASE_PATH;
    const helpCenterManagementScreenWithNewHcCreated = `${helpCenterManagementScreenUrl}?create=true`;
    // Number of help centers to be rendered in the loading skeleton
    const HELP_CENTER_LOADING_SKELETON_LENGTH = 2;
    const renderSuspense = (helpCenterLoadingSkeletonLength: number) => {
        return (
            <>
                {Array(helpCenterLoadingSkeletonLength)
                    .fill(1)
                    .map((_, index) => {
                        return (
                            <Box xcss={skeletonPaddingContainer} key={`multiHCLoader_${index}`}>
                                <LoadingSkeleton
                                    height={16}
                                    backgroundColor={token('color.skeleton.subtle', colors.N20)}
                                />
                            </Box>
                        );
                    })}
            </>
        );
    };

    const truncateDropdownMenuItem = (dropdownItemName: string) =>
        dropdownItemName.length > CHARACTER_LIMIT_MULTI_HC_DROPDOWN_ITEMS
            ? `${dropdownItemName.slice(0, CHARACTER_LIMIT_MULTI_HC_DROPDOWN_ITEMS)}...`
            : dropdownItemName;

    const renderMultiHCDropdownStaticContentWithSpotlightTarget = () => (
        <SpotlightTarget name={EngagementMessagesTarget.MUTLI_HC_CHANGE_BOARDING_SPOTLIGHT}>
            <DropdownItemGroup hasSeparator={isSeparatorVisible}>
                <AnalyticsContext data={{ actionSubjectId: 'manageHelpCenter' }}>
                    <NoUnderlineDropdownItemWithAnalytics
                        href={helpCenterManagementScreenUrl}
                        elemBefore={<EditorSettingsIcon label="" />}
                    >
                        {truncateDropdownMenuItem(formatMessage(messages.manageHc))}
                    </NoUnderlineDropdownItemWithAnalytics>
                </AnalyticsContext>
                <AnalyticsContext data={{ actionSubjectId: 'createHelpCenter' }}>
                    {getIsMultiHCEnabled() ? (
                        <NoUnderlineDropdownItemWithAnalytics
                            href={helpCenterManagementScreenWithNewHcCreated}
                            elemBefore={<EditorAddIcon label="" />}
                        >
                            {truncateDropdownMenuItem(formatMessage(messages.createHc))}
                        </NoUnderlineDropdownItemWithAnalytics>
                    ) : (
                        <NoUnderlineDropdownItemWithAnalytics
                            elemBefore={<EditorAddIcon label="" />}
                            href={'#'}
                            onClick={onCreateHcClick}
                        >
                            {truncateDropdownMenuItem(formatMessage(messages.createHc))}
                        </NoUnderlineDropdownItemWithAnalytics>
                    )}
                </AnalyticsContext>
            </DropdownItemGroup>
        </SpotlightTarget>
    );

    return (
        <>
            <AnalyticsContext data={{ actionSubjectId: 'helpCenterMenu' }}>
                {/* shouldRenderToParent is required here. Refer to this loom - https://www.loom.com/share/b3242f62ee6e4a59b76bfb49e906ab77?sid=df0794e4-776e-475b-ae42-30e7b3da2336*/}
                <DropdownMenu<HTMLButtonElement>
                    onOpenChange={onClickIsOpen}
                    isOpen={showMultiHCChangeBoardingSpotlight || isOpen}
                    label={formatMessage(messages.multiHcLabel)}
                    trigger={({ triggerRef, ...triggerProps }) => (
                        <StyledButton
                            {...triggerProps}
                            ref={triggerRef}
                            appearance="default"
                            iconAfter={<HipchatChevronDownIcon label="" />}
                            aria-label={formatMessage(messages.multiHcLabel)}
                            actionSubjectId="MultiHCDropdownTriggerButton"
                        >
                            {truncateDropdownMenuItem(currentHcName)}
                        </StyledButton>
                    )}
                >
                    {!showMultiHCChangeBoardingSpotlight && (
                        <ErrorBoundary
                            id={'multi-hc-dropdown'}
                            packageName={'help-center-avatar-menu'}
                            renderError={renderError}
                            onError={(error) => {
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                multiHcDropdownExperience.failure({
                                    metadata: { message: error.message },
                                });
                            }}
                        >
                            <LazySuspense fallback={renderSuspense(HELP_CENTER_LOADING_SKELETON_LENGTH)}>
                                {!!queryRef ? (
                                    <MultiHcDropdownContentComponent
                                        toggleSeparatorVisibility={toggleSeparatorVisibility}
                                        renderQueryError={renderError}
                                        onHelpCenterClick={onHelpCenterClick}
                                        queryRef={queryRef}
                                    />
                                ) : null}
                            </LazySuspense>
                        </ErrorBoundary>
                    )}
                    <RenderNextAnimationFrame>
                        {renderMultiHCDropdownStaticContentWithSpotlightTarget}
                    </RenderNextAnimationFrame>
                </DropdownMenu>
            </AnalyticsContext>
        </>
    );
};

const skeletonPaddingContainer = xcss({
    paddingBottom: 'space.100',
    paddingLeft: 'space.200',
    paddingRight: 'space.200',
    paddingTop: 'space.100',
});

const multiHcDropdownErrorStyle = xcss({
    maxWidth: grid.multiple(29).px,
    paddingInline: 'space.100',
});

const StyledButton = styled(Button)({
    width: '100%',
    textAlign: 'left',
});
